import React from "react";

import Byline from "~components/shared/byline/Byline";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
} from "~components/shared/media/";
import Header from "~components/shared/module-header/ModuleHeader";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import { useTranslateMessage } from "~i18n";
import { isOpinionItem } from "~models/transforms/eyebrow";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type CuratedItem,
    type VariationComponent,
    getByline,
    getFranchise,
    getNiCodes,
    getPublishedAt,
    getUpdatedAt,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContent4UpVideo.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContent4UpVideo.module.css";

const MAX_STORIES = 4;
const DEFAULT_VIDEO_IMAGE_ASPECT_RATIO = 16 / 9;

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];

export const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.videoImage];
const MEDIA_CONFIG: MediaConfig = {
    className: styles.image,
    desktopMaxWidth: 300,
    mobileMaxWidth: 150,
    showCaption: false,
    aspectRatio: DEFAULT_VIDEO_IMAGE_ASPECT_RATIO,
};

const StoryBlock = (props: { item: CuratedItem; storyIndex: number }) => {
    const { item, storyIndex } = props;

    const { headline, url, textLabel, labelDropdown, label } = item;

    const byline = getByline(item);
    const franchise = getFranchise(item);
    const nicodes = getNiCodes(item);
    const publishedAt = getPublishedAt(item);
    const updatedAt = getUpdatedAt(item);
    const translateMessage = useTranslateMessage();

    const showName = item?.media?.videoImage?.showName;

    const isOpinionStory = isOpinionItem(item);

    return (
        <div key={url} className={styles.story}>
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <div className={styles.storyContent} data-component="story-block">
                {showName && (
                    <a
                        href={url}
                        className={styles.showLink}
                        data-link-index={storyIndex}
                    >
                        {showName}
                    </a>
                )}
                <a
                    href={url}
                    className={styles.storyLink}
                    data-link-index={storyIndex}
                >
                    {isOpinionStory ? (
                        <Byline
                            byline={byline}
                            isOpinion
                            preText={`${translateMessage({
                                id: "opinionNoColon",
                                defaultMessage: "Opinion",
                            })} | `}
                        />
                    ) : (
                        <OptionalEyebrow
                            label={label}
                            franchise={franchise}
                            updatedAt={updatedAt}
                            publishedAt={publishedAt}
                            textLabel={textLabel}
                            presetLabel={labelDropdown}
                            nicodes={nicodes}
                        />
                    )}
                    <Headline
                        text={headline}
                        isPhoenix
                        size={HEADLINE_SIZE.EXTRA_SMALL}
                    />
                </a>
            </div>
        </div>
    );
};

export default function LineupContent4UpVideo(props: VariationComponent) {
    const { items } = props;

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    return (
        <VariantBase
            source={props}
            className={styles.LineupContent4UpVideo}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <Header {...props} />
            <div className={styles.stories}>
                {items?.slice(0, MAX_STORIES).map((item, index) => {
                    return (
                        <StoryBlock
                            storyIndex={index + 1}
                            item={item}
                            key={item.url}
                        />
                    );
                })}
            </div>
        </VariantBase>
    );
}
